import React, { useState, useEffect } from 'react'
import {
  Table, Button, Space, Card, Form, Input, InputNumber, Select, message, Drawer, Tooltip, Popconfirm, Col,
} from 'antd'
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ThunderboltOutlined, EyeOutlined, HistoryOutlined,
  CalendarOutlined, InfoCircleOutlined, BookOutlined, CheckCircleOutlined, DashboardOutlined, ArrowUpOutlined, SearchOutlined
} from '@ant-design/icons'
import { useMasterData, type TnebTariff } from '@/context/MasterDataContext'
import { useModulePermission } from '@/hooks/useModulePermission'
import { useMasterFormRoute } from '@/hooks/useMasterFormRoute'
import MasterFormShell from '@/components/MasterFormShell'
import TableSkeleton from '@/components/TableSkeleton'
import KpiCardRow from '@/components/KpiCardRow'
import FilterBarCard from '@/components/FilterBarCard'
import { genId } from '@/utils/genId'
import { generateCode } from '@/utils/codeGenerator'
import { ApiError } from '@/services/api'

const BASE = '/master/tneb-tariffs'

const MONO: React.CSSProperties = { fontFamily: 'JetBrains Mono, monospace' }

// Slabs in display order C1 → C5. Each slab has its own rate + Electricity
// Tax (%); "Total" (rate incl. tax) is derived, not stored.
const SLOTS: { key: string; label: string; rateField: keyof TnebTariff; taxField: keyof TnebTariff; color: string }[] = [
  { key: 'C1', label: 'C1 — Night Hour Rate',  rateField: 'nightC1Rate', taxField: 'c1Tax', color: '#2563EB' },
  { key: 'C2', label: 'C2 — Night Hour Rate',  rateField: 'nightC2Rate', taxField: 'c2Tax', color: '#1E3A8A' },
  { key: 'C3', label: 'C3 — Rate',             rateField: 'c3Rate',      taxField: 'c3Tax', color: '#7C3AED' },
  { key: 'C4', label: 'C4 — Normal Hour Rate', rateField: 'ratePerUnit', taxField: 'c4Tax', color: '#059669' },
  { key: 'C5', label: 'C5 — Peak Hour Rate',   rateField: 'peakRate',    taxField: 'c5Tax', color: '#D97706' },
]
const RATE_FIELDS = SLOTS.map(s => s.rateField)
const slotTotal = (rate: number, tax: number) => rate * (1 + tax / 100)

// Back-fills C1-C5 rates/taxes/average for a record saved before this slab
// existed — a stored slab value always wins; only missing ones get a default.
// C3 has no established multiplier of its own, so it defaults to the C4
// (Normal) rate rather than guessing a discount/surcharge.
function withSlotDefaults(rec: TnebTariff): Record<string, any> {
  const normal = rec.ratePerUnit || 0
  const legacyTax = rec.electricityTax ?? 5
  const resolved: Record<string, any> = {
    ...rec,
    peakRate:    rec.peakRate    ?? parseFloat((normal * 1.20).toFixed(2)),
    nightC1Rate: rec.nightC1Rate ?? parseFloat((normal * 0.95).toFixed(2)),
    nightC2Rate: rec.nightC2Rate ?? parseFloat((normal * 0.90).toFixed(2)),
    c3Rate:      rec.c3Rate      ?? normal,
    c1Tax: rec.c1Tax ?? legacyTax,
    c2Tax: rec.c2Tax ?? legacyTax,
    c3Tax: rec.c3Tax ?? legacyTax,
    c4Tax: rec.c4Tax ?? legacyTax,
    c5Tax: rec.c5Tax ?? legacyTax,
  }
  const avg = RATE_FIELDS.reduce((s, f) => s + (Number(resolved[f]) || 0), 0) / RATE_FIELDS.length
  resolved.averageRate = rec.averageRate ?? parseFloat(avg.toFixed(2))
  return resolved
}

const DetailRow: React.FC<{ label: string; value?: React.ReactNode }> = ({ label, value }) => (
  <div style={{ padding: '10px 0', borderBottom: '1px solid #f0f0f0', display: 'flex', gap: 12 }}>
    <div style={{ width: 140, flexShrink: 0, fontSize: 12, color: '#8A93A6', fontWeight: 500 }}>{label}</div>
    <div style={{ fontSize: 13, color: '#0B1426', flex: 1 }}>{value || <span style={{ color: '#ccc' }}>—</span>}</div>
  </div>
)

const TnebTariffMaster: React.FC = () => {
  const { tnebTariffs, tnebCategories, deleteTnebTariff, saveTnebTariff, masterDataLoaded } = useMasterData()
  const categoryOptions = tnebCategories.filter(c => c.status === 'active').map(c => c.name)
  const { canAdd, canEdit, canDelete } = useModulePermission('Masters')
  const { mode, editId, isForm, goAdd, goEdit, goList } = useMasterFormRoute(BASE)
  const [viewRecord, setViewRecord] = useState<TnebTariff | null>(null)
  const [reviseFrom, setReviseFrom] = useState<string | null>(null)
  const [form]                  = Form.useForm()
  const [submitting, setSubmitting] = useState(false)
  const [formData, setFormData] = useState<any>({})
  const [searchText, setSearchText] = useState('')
  const [filterStatus, setFilterStatus] = useState<string>('')

  const activeCount = tnebTariffs.filter(t => t.status === 'active').length
  const avgRate     = tnebTariffs.filter(t => t.status === 'active').reduce((s, t) => s + Number(t.ratePerUnit), 0) / (activeCount || 1)
  const maxRate     = tnebTariffs.length ? Math.max(...tnebTariffs.map(t => Number(t.ratePerUnit))) : 0

  const filteredTnebTariffs = tnebTariffs.filter(t => {
    const q = searchText.toLowerCase()
    const matchSearch = !q ||
      t.code.toLowerCase().includes(q) ||
      (t.description || '').toLowerCase().includes(q) ||
      (t.category || '').toLowerCase().includes(q)
    const matchStatus = !filterStatus || t.status === filterStatus
    return matchSearch && matchStatus
  })

  // Populate the form when entering the full-page add/edit route
  useEffect(() => {
    if (mode === 'new') {
      // A revise seed (set by openRevise before navigating) survives the reset below.
      if (!reviseFrom) {
        form.resetFields()
        setFormData(form.getFieldsValue())
      }
    } else if (mode === 'edit') {
      const rec = tnebTariffs.find(t => t.id === editId)
      if (rec) {
        const initialVal = withSlotDefaults(rec)
        form.setFieldsValue(initialVal)
        setFormData(initialVal)
      } else {
        goList()
      }
    }
    // clear a stale revise seed whenever we leave the new route
    if (mode !== 'new' && reviseFrom) setReviseFrom(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, editId, tnebTariffs])

  const openRevise = (t: TnebTariff) => {
    setReviseFrom(t.id)
    form.resetFields()
    const initialVal = {
      ...withSlotDefaults(t),
      id: undefined,
      effectiveFrom: new Date().toISOString().slice(0, 10),
      status: 'active',
    }
    form.setFieldsValue(initialVal)
    setFormData(initialVal)
    goAdd()
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteTnebTariff(id)
      message.success('Removed')
      if (isForm) goList()
    } catch { /* error already shown */ }
  }

  const onFinish = async (values: any) => {
    // keep nightRate/electricityTax in sync with C1/C4 for SavingsCalculator compat
    const finalValues = { ...values, nightRate: values.nightC1Rate, electricityTax: values.c4Tax }

    // A revision (reviseFrom) or an edit keeps the tariff's existing code
    // (e.g. "HT1") — only a genuinely new tariff gets a freshly generated
    // one. UNIQUE_KEYS allows repeats of the same code as long as
    // effectiveFrom differs, which is exactly how revisions coexist.
    let code = finalValues.code
    if (!reviseFrom && mode !== 'edit') {
      try {
        code = await generateCode('tneb_tariffs')
      } catch (err) {
        message.error(err instanceof ApiError ? err.message : "Couldn't generate a code — check your connection and try again.")
        return
      }
    }

    setSubmitting(true)
    try {
      if (reviseFrom) {
        const oldRecord = tnebTariffs.find(t => t.id === reviseFrom)
        if (oldRecord) await saveTnebTariff({ ...oldRecord, status: 'superseded' }, true)
        await saveTnebTariff({ ...finalValues, code, id: genId('TN') }, false)
        message.success('TNEB tariff revised successfully')
      } else if (mode === 'edit') {
        await saveTnebTariff({ ...finalValues, code, id: editId }, true)
        message.success('TNEB tariff updated')
      } else {
        await saveTnebTariff({ ...finalValues, code, id: genId('TN') }, false)
        message.success('TNEB tariff added')
      }
      setReviseFrom(null)
      goList()
    } catch { /* error already shown; stay on the form */ }
    finally { setSubmitting(false) }
  }

  const columns = [
    {
      title: 'Code', dataIndex: 'code', width: 120,
      render: (v: string) => (
        <span style={{ ...MONO, fontWeight: 700, color: '#1677ff', fontSize: 13 }}>
          {v}
        </span>
      ),
    },
    {
      title: 'Category', dataIndex: 'category', width: 110,
      render: (v: string) => <span className="pill pill-info">{v}</span>,
    },
    {
      title: 'Description', dataIndex: 'description',
      render: (v: string) => <span style={{ fontWeight: 500 }}>{v}</span>,
    },
    {
      title: 'C4 Normal (₹/kWh)', dataIndex: 'ratePerUnit', align: 'right' as const,
      render: (v: number | string) => (
        <span style={{ ...MONO, fontWeight: 700, fontSize: 15, color: '#DC2626' }}>₹{Number(v).toFixed(2)}</span>
      ),
    },
    {
      title: 'Avg Rate (₹/kWh)', dataIndex: 'averageRate', align: 'right' as const,
      render: (v: number | string | undefined) => (
        v ? <span style={{ ...MONO, fontWeight: 700, fontSize: 13, color: '#059669' }}>₹{Number(v).toFixed(2)}</span> : <span style={{ color: '#ccc' }}>—</span>
      ),
    },
    {
      title: 'C4 Tax', dataIndex: 'c4Tax', align: 'right' as const,
      render: (_: any, r: TnebTariff) => {
        const v = r.c4Tax ?? r.electricityTax
        return v !== undefined ? <span style={{ fontWeight: 600 }}>{v}%</span> : <span style={{ color: '#ccc' }}>—</span>
      },
    },
    {
      title: 'Effective From', dataIndex: 'effectiveFrom',
      render: (v: string) => <span style={{ ...MONO, fontSize: 12 }}>{v}</span>,
    },
    {
      title: 'Status', dataIndex: 'status',
      render: (s: string) => (
        <span className={`pill ${s === 'active' ? 'pill-green' : 'pill-neutral'}`}>
          {s === 'active' ? 'Active' : 'Superseded'}
        </span>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 130,
      render: (_: any, record: TnebTariff) => (
        <Space size="small" onClick={e => e.stopPropagation()}>
          <Tooltip title="View">
            <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)} />
          </Tooltip>
          {canEdit && (
            <Tooltip title="Edit">
              <Button type="link" size="small" icon={<EditOutlined />} onClick={() => goEdit(record.id)} />
            </Tooltip>
          )}
          {canEdit && record.status === 'active' && (
            <Tooltip title="Revise Tariff">
              <Button type="link" size="small" icon={<HistoryOutlined />} onClick={() => openRevise(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <Popconfirm
              title="Delete this record?"
              description="This action cannot be undone."
              onConfirm={() => handleDelete(record.id)}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Tooltip title="Delete">
                <Button type="link" danger size="small" icon={<DeleteOutlined />} />
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ]

  // ── Full-page Add / Edit form ──────────────────────────────────
  if (isForm) {
    const previewCode = formData.code || '—'
    const previewCategory = formData.category || 'HT1'
    const previewDescription = formData.description || '—'
    const averageRate = Number(formData.averageRate) || 0
    const statusVal = formData.status || 'active'
    const effectiveDate = formData.effectiveFrom || '—'

    // Live rate/tax/total per slab, in C1 → C5 order
    const slotPreview = SLOTS.map(s => {
      const rate = Number(formData[s.rateField]) || 0
      const tax = formData[s.taxField] !== undefined ? Number(formData[s.taxField]) : 5
      return { ...s, rate, tax, total: slotTotal(rate, tax) }
    })

    // Compute max for progress scale to make it relative
    const maxVal = Math.max(...slotPreview.map(s => s.total), 10)
    const getPercent = (v: number) => Math.min(100, Math.max(0, (v / maxVal) * 100))

    return (
      <MasterFormShell
        title={reviseFrom ? 'Revise TNEB Tariff' : mode === 'edit' ? 'Edit TNEB Tariff' : 'Add TNEB Tariff'}
        subtitle="TANGEDCO grid tariff rate"
        onBack={goList}
        extra={mode === 'edit' && editId ? (
          <Popconfirm
            title="Delete this record?"
            description="This action cannot be undone."
            onConfirm={() => handleDelete(editId)}
            okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>Delete</Button>
          </Popconfirm>
        ) : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={onFinish}
          onValuesChange={(changedValues, allValues) => {
            let updated = { ...allValues }

            // Recompute the C1-C5 average whenever any slab rate changed
            if (RATE_FIELDS.some(f => f in changedValues)) {
              const sum = RATE_FIELDS.reduce((s, f) => s + (Number(updated[f]) || 0), 0)
              const avg = parseFloat((sum / RATE_FIELDS.length).toFixed(2))
              form.setFieldsValue({ averageRate: avg })
              updated = { ...updated, averageRate: avg }
            }

            setFormData(updated)
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.3fr) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
            
            {/* Left side: Form fields */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
              
              {/* General Details card-like section */}
              <div style={{ border: '1px solid #E4E8F0', borderRadius: 8, padding: 20, background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(0,26,77,0.06)', color: 'var(--brand-primary)' }}>
                    <InfoCircleOutlined style={{ fontSize: 16 }} />
                  </span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-ink)' }}>General Details</h3>
                    <span style={{ fontSize: 12, color: 'var(--text-ink-3)' }}>Specify the tariff code, category, and effective date</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                  <Form.Item name="code" label="Tariff Code">
                    <Input disabled placeholder="Auto-generated on save" style={{ ...MONO, fontWeight: 700 }} />
                  </Form.Item>
                  <Form.Item name="category" label="Category" rules={[{ required: true }]} initialValue="HT1">
                    <Select options={categoryOptions.map(c => ({ value: c, label: c }))} />
                  </Form.Item>
                  <Form.Item name="status" label="Status" initialValue="active">
                    <Select options={[{ value: 'active', label: 'Active' }, { value: 'superseded', label: 'Superseded' }]} />
                  </Form.Item>
                  <Form.Item name="effectiveFrom" label="Effective From" rules={[{ required: true }]}>
                    <Input type="date" style={MONO} />
                  </Form.Item>
                </div>
                <Form.Item name="description" label="Description" rules={[{ required: true }]} style={{ marginBottom: 0 }}>
                  <Input placeholder="e.g. HT Industrial" />
                </Form.Item>
              </div>

              {/* Pricing & Slabs card-like section */}
              <div style={{ border: '1px solid #E4E8F0', borderRadius: 8, padding: 20, background: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 32, height: 32, borderRadius: '50%', backgroundColor: 'rgba(5,150,105,0.08)', color: 'var(--brand-green)' }}>
                    <ThunderboltOutlined style={{ fontSize: 16 }} />
                  </span>
                  <div>
                    <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--text-ink)' }}>Tariff Rates & Slabs</h3>
                    <span style={{ fontSize: 12, color: 'var(--text-ink-3)' }}>C1 → C5, each with its own rate and Electricity Tax (%)</span>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '56px 1fr 1fr 1fr', gap: '8px 12px', alignItems: 'center' }}>
                  <div />
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6' }}>Rate (₹/kWh)</div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6' }}>Electricity Tax (%)</div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6' }}>Total (₹/kWh)</div>

                  {SLOTS.map(s => (
                    <React.Fragment key={s.key}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: 8, background: `${s.color}1A`, color: s.color, fontWeight: 800, fontSize: 13 }}>
                        {s.key}
                      </div>
                      <Form.Item
                        name={s.rateField}
                        rules={s.key === 'C4' ? [{ required: true, message: 'Required' }] : undefined}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber
                          min={0} step={0.01} precision={2} style={{ width: '100%' }}
                          prefix={<span style={{ color: s.color, fontWeight: 600 }}>₹</span>}
                        />
                      </Form.Item>
                      <Form.Item
                        name={s.taxField}
                        initialValue={5}
                        rules={s.key === 'C4' ? [{ required: true, message: 'Required' }] : undefined}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={0} max={100} step={1} precision={1} style={{ width: '100%' }} addonAfter="%" />
                      </Form.Item>
                      <Form.Item shouldUpdate style={{ marginBottom: 0 }} noStyle>
                        {() => {
                          const rate = Number(form.getFieldValue(s.rateField)) || 0
                          const tax = form.getFieldValue(s.taxField)
                          const total = slotTotal(rate, tax !== undefined ? Number(tax) : 5)
                          return (
                            <div style={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, color: 'var(--text-ink)', background: '#F8FAFC', border: '1px solid #E4E8F0', borderRadius: 6, padding: '4px 10px', textAlign: 'right' }}>
                              ₹{total.toFixed(2)}
                            </div>
                          )
                        }}
                      </Form.Item>
                    </React.Fragment>
                  ))}
                </div>

                <Form.Item name="averageRate" label="Average Price — C1 to C5 (₹/kWh)" style={{ marginTop: 16, marginBottom: 0, maxWidth: 260 }}>
                  <InputNumber
                    readOnly
                    style={{ width: '100%', background: '#F8FAFC' }}
                    prefix={<span style={{ color: '#0F172A', fontWeight: 600 }}>₹</span>}
                    precision={2}
                  />
                </Form.Item>
              </div>
            </div>

            {/* Right side: Live Tariff Preview Card */}
            <div style={{ position: 'sticky', top: 24, border: '1px solid #CED4E0', borderRadius: 12, overflow: 'hidden', boxShadow: '0 8px 30px rgba(11,20,38,0.06)', background: '#FAFBFD' }}>
              <div style={{ background: 'linear-gradient(135deg, #001A4D 0%, #002775 100%)', padding: '20px 24px', color: '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase', color: 'rgba(255,255,255,0.7)' }}>TARIFF SPECIFICATION SHEET</span>
                  <span className={`pill ${statusVal === 'active' ? 'pill-green' : 'pill-neutral'}`} style={{ color: statusVal === 'active' ? '#059669' : '#4A5568', background: statusVal === 'active' ? '#E6F4EA' : '#F1F3F5', border: 'none', padding: '2px 8px' }}>
                    {statusVal === 'active' ? 'Active' : 'Superseded'}
                  </span>
                </div>
                <h2 style={{ margin: 0, fontSize: 24, fontWeight: 800, letterSpacing: -0.5, color: '#fff', fontFamily: 'JetBrains Mono, monospace' }}>
                  {previewCode}
                </h2>
                <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.85)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ padding: '2px 6px', background: 'rgba(255,255,255,0.15)', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{previewCategory}</span>
                  <span>{previewDescription}</span>
                </div>
              </div>

              <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 20 }}>
                {/* Hero display for Average Rate */}
                <div style={{ textAlign: 'center', background: '#fff', border: '1px solid #E4E8F0', borderRadius: 8, padding: '16px 20px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.02)' }}>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6', letterSpacing: 0.8 }}>Calculated Average Tariff</div>
                  <div style={{ fontSize: 32, fontWeight: 800, color: 'var(--brand-primary)', margin: '4px 0', fontFamily: 'JetBrains Mono, monospace' }}>
                    ₹{averageRate.toFixed(2)}<span style={{ fontSize: 14, fontWeight: 500, color: '#8A93A6' }}> / kWh</span>
                  </div>
                  <div style={{ fontSize: 12, color: '#059669', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                    <span>C1, C2, C3, C4, C5 average rate</span>
                  </div>
                </div>

                {/* Slabs Bar Chart — C1 → C5, with per-slab tax and total */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', color: '#8A93A6', letterSpacing: 0.8, marginBottom: 12 }}>Tariff Slab Breakdown</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                    {slotPreview.map(s => (
                      <div key={s.key}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
                          <span style={{ fontWeight: 600, color: 'var(--text-ink-2)' }}>{s.label}</span>
                          <span style={{ ...MONO, fontWeight: 700, color: s.color }}>₹{s.rate.toFixed(2)} <span style={{ color: '#8A93A6', fontWeight: 500 }}>+{s.tax}%</span> = ₹{s.total.toFixed(2)}</span>
                        </div>
                        <div style={{ height: 6, background: '#E2E8F0', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ width: `${getPercent(s.total)}%`, height: '100%', background: s.color, borderRadius: 3, transition: 'width 0.3s ease' }} />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #E4E8F0', paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: '#8A93A6' }}>Effective From</span>
                    <span style={{ ...MONO, fontWeight: 600, color: 'var(--text-ink)' }}>
                      <CalendarOutlined style={{ marginRight: 4, fontSize: 11 }} />
                      {effectiveDate}
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={goList} disabled={submitting}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {reviseFrom ? 'Revise TNEB Tariff' : mode === 'edit' ? 'Update TNEB Tariff' : 'Add TNEB Tariff'}
            </Button>
          </div>
        </Form>
      </MasterFormShell>
    )
  }

  // ── Full-page Detail View ──────────────────────────────────────
  if (viewRecord) {
    return (
      <MasterFormShell
        title={`View TNEB Tariff — ${viewRecord.code}`}
        subtitle="TANGEDCO grid tariff details"
        onBack={() => setViewRecord(null)}
        extra={
          <Space>
            <Button type="primary" icon={<EditOutlined />} onClick={() => { goEdit(viewRecord.id); setViewRecord(null); }}>Edit</Button>
            {viewRecord.status === 'active' && (
              <Button icon={<HistoryOutlined />} onClick={() => { openRevise(viewRecord); setViewRecord(null); }}>Revise Tariff</Button>
            )}
            <Popconfirm
              title="Delete this record?"
              description="This action cannot be undone."
              onConfirm={() => { handleDelete(viewRecord.id); setViewRecord(null); }}
              okText="Delete" cancelText="Cancel" okButtonProps={{ danger: true }}
            >
              <Button danger icon={<DeleteOutlined />}>Delete</Button>
            </Popconfirm>
          </Space>
        }
      >
        <div style={{ maxWidth: 640 }}>
          <div style={{ marginBottom: 20 }}>
            <span className={`pill ${viewRecord.status === 'active' ? 'pill-green' : 'pill-neutral'}`}>
              {viewRecord.status === 'active' ? 'Active' : 'Superseded'}
            </span>
            <span style={{ marginLeft: 8 }} className="pill pill-info">{viewRecord.category}</span>
          </div>
          <DetailRow label="Tariff Code" value={<span style={MONO}>{viewRecord.code}</span>} />
          {SLOTS.map(s => {
            const rate = Number(viewRecord[s.rateField as keyof TnebTariff]) || 0
            const tax = viewRecord[s.taxField as keyof TnebTariff]
            const taxVal = tax !== undefined ? Number(tax) : (viewRecord.electricityTax ?? 5)
            return (
              <DetailRow
                key={s.key}
                label={s.label}
                value={
                  <span style={{ ...MONO, fontWeight: 700, color: s.color }}>
                    ₹{rate.toFixed(2)}/kWh <span style={{ color: '#8A93A6', fontWeight: 500 }}>+{taxVal}% tax</span> = ₹{slotTotal(rate, taxVal).toFixed(2)}/kWh
                  </span>
                }
              />
            )
          })}
          <DetailRow label="Average Rate" value={<span style={{ ...MONO, fontWeight: 700, color: '#059669' }}>₹{Number(viewRecord.averageRate || 0).toFixed(2)}/kWh</span>} />
          <DetailRow label="Category" value={viewRecord.category} />
          <DetailRow label="Effective From" value={<span style={MONO}>{viewRecord.effectiveFrom}</span>} />
          <DetailRow label="Description" value={viewRecord.description} />
          
          <div style={{ display: 'flex', gap: 8, marginTop: 24, borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
            <Button onClick={() => setViewRecord(null)}>Back to List</Button>
          </div>
        </div>
      </MasterFormShell>
    )
  }

  // ── List ───────────────────────────────────────────────────────
  if (!masterDataLoaded) return <TableSkeleton columns={9} />

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <ThunderboltOutlined style={{ color: '#DC2626' }} /> TNEB Tariff Master
          </h2>
          <p className="page-subtitle">TANGEDCO grid tariff — the base rate against which Shakthi Power savings are computed</p>
        </div>
        {canAdd && <Button type="primary" icon={<PlusOutlined />} onClick={goAdd}>Add TNEB Tariff</Button>}
      </div>

      <KpiCardRow
        cards={[
          { label: 'Total Tariffs', value: tnebTariffs.length, icon: <BookOutlined style={{ fontSize: 24, color: '#001A4D' }} />, iconBg: 'rgba(0,26,77,0.08)', borderColor: '#001A4D' },
          { label: 'Active', value: activeCount, icon: <CheckCircleOutlined style={{ fontSize: 24, color: '#059669' }} />, iconBg: 'rgba(5,150,105,0.08)', borderColor: '#059669' },
          { label: 'Avg Grid Rate', value: `₹${avgRate.toFixed(2)}/kWh`, icon: <DashboardOutlined style={{ fontSize: 24, color: '#F59E0B' }} />, iconBg: 'rgba(245,158,11,0.08)', borderColor: '#F59E0B' },
          { label: 'Highest Rate', value: `₹${maxRate.toFixed(2)}/kWh`, icon: <ArrowUpOutlined style={{ fontSize: 24, color: '#DC2626' }} />, iconBg: 'rgba(220,38,38,0.08)', borderColor: '#DC2626' },
        ]}
      />

      <FilterBarCard>
        <Col xs={24} sm={12} lg={10}>
          <Input.Search
            placeholder="Search by code, category or description..."
            allowClear
            prefix={<SearchOutlined style={{ color: '#8A93A6' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onSearch={(v) => setSearchText(v)}
          />
        </Col>
        <Col xs={24} sm={12} lg={7}>
          <Select
            placeholder="Filter by Status"
            allowClear
            value={filterStatus || undefined}
            onChange={(v) => setFilterStatus(v || '')}
            style={{ width: '100%' }}
            options={[{ label: 'Active', value: 'active' }, { label: 'Superseded', value: 'superseded' }]}
          />
        </Col>
      </FilterBarCard>

      <Card>
        <Table
          dataSource={filteredTnebTariffs}
          columns={columns}
          rowKey="id"
          pagination={{ defaultPageSize: 10, pageSizeOptions: ['10', '25', '50', '100'], showSizeChanger: true, showTotal: (t: number) => `${t} tariffs` }}
        />
      </Card>
    </div>
  )
}

export default TnebTariffMaster
